import { useState, useEffect } from 'react';
import { validateUsername, hasInvalidUsernameChars } from '../utils/validation';
import styles from './ValidatedInput.module.css';

/**
 * Username input field with real-time validation and character filtering
 * - Enforces alphanumeric-only input
 * - Real-time length validation
 * - Shows temporary tooltip when invalid character is typed
 */
export function UsernameInput({
    value,
    onChange,
    error,
    onValidationChange,
    placeholder = 'Brugernavn',
    disabled = false
}) {
    const [touched, setTouched] = useState(false);
    const [validationResult, setValidationResult] = useState(null);
    const [showInvalidCharTooltip, setShowInvalidCharTooltip] = useState(false);

    // Validate on change
    useEffect(() => {
        if (value) {
            const result = validateUsername(value);
            setValidationResult(result);
            if (onValidationChange) {
                onValidationChange(result.valid);
            }
        } else {
            setValidationResult(null);
            if (onValidationChange) {
                onValidationChange(false);
            }
        }
    }, [value, onValidationChange]);

    useEffect(() => {
        if (!showInvalidCharTooltip) return;

        const timer = setTimeout(() => {
            setShowInvalidCharTooltip(false);
        }, 1500);

        return () => clearTimeout(timer);
    }, [showInvalidCharTooltip]);

    const handleChange = (e) => {
        let newValue = e.target.value;
        const oldValue = value;

        // Check if any invalid characters were typed
        const hasInvalid = newValue
            .split('')
            .some(char => hasInvalidUsernameChars(char));

        // Filter out invalid characters
        newValue = newValue
            .split('')
            .filter(char => !hasInvalidUsernameChars(char))
            .join('');

        // Enforce max length
        newValue = newValue.slice(0, 30);

        // Show tooltip if invalid character was typed
        if (hasInvalid && newValue === oldValue) {
            setShowInvalidCharTooltip(true);
            return;
        }

        onChange(newValue);
    };

    const handleBlur = () => {
        setTouched(true);
    };

    const showError = touched && validationResult && !validationResult.valid;
    const showSuccess = touched && validationResult && validationResult.valid && value.length >= 3;

    return (
        <div className={styles.fieldWrapper}>
            {showInvalidCharTooltip && (
                <div className={styles.tooltip}>
                    Kun bogstaver og tal
                </div>
            )}
            <input
                type="text"
                placeholder={placeholder}
                value={value}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={disabled}
                className={`${styles.input} ${showError ? styles.inputError : ''} ${showSuccess ? styles.inputSuccess : ''}`}
                maxLength={30}
                autoComplete="username"
            />
            {value && (
                <div className={styles.charCount}>
                    {value.length}/30
                </div>
            )}
            {showError && (
                <div className={styles.errorMessage}>
                    ⚠️ {validationResult.error}
                </div>
            )}
            {showSuccess && (
                <div className={styles.successMessage}>
                    ✓ Brugernavn accepteret
                </div>
            )}
            {error && (
                <div className={styles.serverError}>
                    {error}
                </div>
            )}
        </div>
    );
}
